import React, { AriaRole, useCallback } from 'react';
import styled from 'styled-components';

import { BORDER_RADIUS, BORDER_WIDTH, PADDING_BY_SIZE } from '@front/common/src/components/selectable-card/constants';
import {
  SelectableCardProps as SelectableCardPropsType,
  SelectableCardSize,
} from '@front/common/src/components/selectable-card/type';

import Card from '../card/card';
import { borderGradient100 } from '../../styles/gradient';
import { WithClassNameProps } from '../types';

const StyledCard = styled(Card)<{
  $size: SelectableCardSize;
  $hasFullWidth: boolean;
  $isSelected: boolean;
}>`
  width: ${({ $hasFullWidth }) => ($hasFullWidth ? '100%' : 'fit-content')};
  padding: ${({ $size }) => PADDING_BY_SIZE[$size] - BORDER_WIDTH}px;
  background: ${({ $isSelected }) => $isSelected && borderGradient100};
  border: ${BORDER_WIDTH}px solid transparent;
  border-radius: ${BORDER_RADIUS}px;
  display: flex;
  flex-direction: ${({ $size }) => ($size === 'lg' ? 'column' : 'row')};
  justify-content: center;
  align-items: ${({ $size }) => ($size === 'lg' ? 'flex-start' : 'center')};
  height: ${({ $size }) => ($size === 'sm' ? '46px' : 'auto')};
`;

export type SelectableCardProps<T> = SelectableCardPropsType<T> &
  WithClassNameProps & { label?: string; role?: AriaRole; testId?: string };

const SelectableCard = <T extends any>({
  label,
  className,
  role = 'checkbox',
  size = 'md',
  isSelected = false,
  onPress,
  value,
  hasFullWidth = false,
  children,
  testId,
}: SelectableCardProps<T>) => {
  const onCardPress = useCallback(() => {
    onPress(value);
  }, [onPress, value]);

  return (
    <StyledCard
      label={label}
      role={role}
      selected={isSelected}
      onPress={onCardPress}
      $size={size}
      $hasFullWidth={hasFullWidth}
      $isSelected={isSelected}
      className={className}
      testId={testId}
    >
      {children}
    </StyledCard>
  );
};

export default SelectableCard;
